import { useMutation, useQuery } from '@tanstack/react-query'
import { useUserData } from 'auth'
import Select, { Option } from 'components/select'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Button from 'components/button'
import { SizableText, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { preloadDocusignDigitalSignup, startDocusignTrial, viewDocusignPlans } from 'utils/dsDigitalSignup'

const prod = import.meta.env.VITE_APP_STAGE === 'prod'
const docusignAuthServer = `https://account${prod ? '' : '-d'}.docusign.com`

/**
 * Build the DocuSign OAuth consent URL for the current user/org. Used both by the
 * standalone connect button and by the service selector, which redirects here when
 * DocuSign is chosen but no account is connected yet.
 */
const useDocusignConnect = () => {
    const trpc = useTRPC()
    const getStateMutation = useMutation(trpc.docusignSettings.getConnectState.mutationOptions())
    const { VITE_APP_STAGE } = import.meta.env

    const redirectUri =
        (import.meta.env.DEV
            ? 'https://qchkocg5lj.execute-api.ap-southeast-2.amazonaws.com'
            : `https://api.${VITE_APP_STAGE === 'prod' ? '' : VITE_APP_STAGE + '.'}buyerflo.com.au`) +
        '/docusign/authredirect'

    const integrationKey = import.meta.env.VITE_DOCUSIGN_INTEGRATION_ID
    const scopes = ['signature', 'impersonation']

    // Fetch a server-issued signed `state` (CSRF protection) before building the
    // consent URL; the redirect handler verifies it before storing tokens.
    return async () => {
        const { state } = await getStateMutation.mutateAsync()
        return `${docusignAuthServer}/oauth/auth?response_type=code&scope=${scopes.join(' ')}&client_id=${integrationKey}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`
    }
}

const DocuSignSection = () => {
    const trpc = useTRPC()
    const { user, refreshUser } = useUserData()
    const [loading, setLoading] = useState(false)

    const myDocusignStatus = useQuery(trpc.docusignSettings.getMyDocusignStatus.queryOptions())
    const disconnectMutation = useMutation(trpc.docusignSettings.disconnect.mutationOptions())

    const connected = myDocusignStatus.data?.connected

    const getDocusignConnectUrl = useDocusignConnect()

    // Warm up DocuSign's Digital Referral script so the trial / plans popups can
    // open synchronously from the click handler (avoids the browser popup blocker).
    useEffect(() => preloadDocusignDigitalSignup(), [])

    return (
        <View data-tour="esign-connect">
            <SizableText
                fontSize="$5"
                fontWeight="600"
                marginBottom="$2"
            >
                DocuSign
            </SizableText>
            <SizableText
                fontSize="$2"
                marginBottom="$3"
                color="$gray10"
            >
                Connect your DocuSign account. Forms will be sent from the assigned agent's account.
            </SizableText>
            {connected ? (
                <YStack gap="$2">
                    <XStack
                        alignItems="center"
                        gap="$2"
                    >
                        <Text>Connected</Text>
                        <View
                            paddingHorizontal="$2"
                            paddingVertical="$1"
                            borderRadius="$3"
                            backgroundColor="$primaryMuted"
                        >
                            <SizableText fontSize="$1">Active</SizableText>
                        </View>
                    </XStack>
                    <SizableText
                        fontSize="$2"
                        color="$gray10"
                    >
                        Account: {myDocusignStatus.data?.accountName || myDocusignStatus.data?.accountId}
                    </SizableText>
                    <XStack
                        gap="$2"
                        marginTop="$2"
                        flexWrap="wrap"
                    >
                        {/* On a trial account? View plans to purchase a paid DocuSign subscription. */}
                        <Button
                            alignSelf="flex-start"
                            variant="outlined"
                            size="$3"
                            onPress={() => viewDocusignPlans()}
                        >
                            View DocuSign plans
                        </Button>
                        <Button
                            alignSelf="flex-start"
                            variant="outlined"
                            theme="red"
                            size="$3"
                            onPress={async () => {
                                await disconnectMutation.mutateAsync()
                                await Promise.all([myDocusignStatus.refetch(), refreshUser()])
                            }}
                        >
                            Disconnect
                        </Button>
                    </XStack>
                </YStack>
            ) : (
                <YStack gap="$4">
                    <Button
                        size="$3"
                        backgroundColor="$green8"
                        style={{ maxWidth: '200px' }}
                        // Keep the spinner up through the redirect: `loading` stays true after
                        // the URL resolves so the button shows progress while the browser
                        // navigates away, and only resets if building the URL fails.
                        loading={loading}
                        disabled={myDocusignStatus.isLoading}
                        data-help-id="settings-esign-connect"
                        onPress={async () => {
                            setLoading(true)
                            try {
                                window.location.href = await getDocusignConnectUrl()
                            } catch {
                                setLoading(false)
                            }
                        }}
                    >
                        Connect to DocuSign
                    </Button>
                    {/*
                     * Digital Referral: users without a DocuSign account can start a free
                     * trial here. The button opens DocuSign's signup workflow (DSDigitalSignup)
                     * in a popup, pre-filled with the current user's details.
                     */}
                    <YStack
                        gap="$2"
                        borderTopWidth={1}
                        borderColor="$borderColor"
                        paddingTop="$4"
                    >
                        <SizableText
                            fontSize="$2"
                            color="$gray10"
                        >
                            Don't have a DocuSign account? Try the integration with a free DocuSign trial.
                        </SizableText>
                        <Button
                            size="$3"
                            variant="outlined"
                            style={{ maxWidth: '200px' }}
                            data-help-id="settings-esign-trial"
                            onPress={() =>
                                startDocusignTrial({
                                    firstName: user.firstName,
                                    lastName: user.lastName,
                                    email: user.email,
                                })
                            }
                        >
                            Get DocuSign for Free
                        </Button>
                    </YStack>
                </YStack>
            )}
        </View>
    )
}

const SigningServiceSelector = () => {
    const trpc = useTRPC()
    const { user, refreshUser } = useUserData()
    const [loading, setLoading] = useState(false)

    const myDocusignStatus = useQuery(trpc.docusignSettings.getMyDocusignStatus.queryOptions())
    const setServiceMutation = useMutation(trpc.docusignSettings.setSigningService.mutationOptions())
    const getDocusignConnectUrl = useDocusignConnect()

    const currentService = user.signingService ?? 'annature'

    return (
        <View
            maxWidth={500}
            data-tour="esign-service"
        >
            <SizableText
                fontSize="$5"
                fontWeight="600"
                marginBottom="$2"
            >
                Signing Service
            </SizableText>
            <SizableText
                fontSize="$2"
                marginBottom="$4"
                color="$gray10"
            >
                Choose which e-signing service to use for new forms. Existing in-flight forms will continue
                with their original service.
            </SizableText>
            <Select
                value={currentService}
                disabled={loading || myDocusignStatus.isLoading}
                data-help-id="settings-esign-service"
                onChange={async (_: any, value: any) => {
                    if (!value || value === currentService) return

                    // Switching to DocuSign requires a connected DocuSign account. If
                    // the user hasn't connected yet, push them through the OAuth consent
                    // flow instead of switching now — the signing service is only set to
                    // DocuSign once the connection succeeds (handled server-side by the
                    // /docusign/authredirect handler on the way back).
                    if (value === 'docusign' && !myDocusignStatus.data?.connected) {
                        setLoading(true)
                        try {
                            window.location.href = await getDocusignConnectUrl()
                        } catch {
                            setLoading(false)
                        }
                        return
                    }

                    setLoading(true)
                    try {
                        await setServiceMutation.mutateAsync({ service: value })
                        await refreshUser()
                    } finally {
                        setLoading(false)
                    }
                }}
            >
                <Option value="annature">Annature</Option>
                <Option value="docusign">DocuSign</Option>
            </Select>
        </View>
    )
}

export default () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const error = searchParams.get('error')

    return (
        <YStack gap="$6">
            {error && (
                <XStack
                    backgroundColor="$red3"
                    padding="$3"
                    borderRadius="$3"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Text>DocuSign connection failed: {error}</Text>
                    <Button
                        size="$2"
                        theme="red"
                        variant="outlined"
                        onPress={() => {
                            searchParams.delete('error')
                            setSearchParams(searchParams, { replace: true })
                        }}
                    >
                        Dismiss
                    </Button>
                </XStack>
            )}
            <SigningServiceSelector />
            <DocuSignSection />
        </YStack>
    )
}
